import { isFirestoreSafeOrganizationId, type OrganizationLocaleCode } from "./organization.js";

export const organizationEventStatusIds = [
  "draft",
  "scheduled",
  "published",
  "registration_closed",
  "cancelled",
  "completed",
  "archived",
] as const;
export const organizationEventVisibilityIds = [
  "public",
  "connected",
  "roles",
  "invitation_only",
] as const;
export const organizationEventTypeIds = ["standard", "friday_program", "janazah"] as const;
export const organizationEventSourceIds = [
  "development_seed",
  "experience_twenty_three",
  "migration_hvicc",
  "p13_events",
  "p14_events",
] as const;
export const organizationEventNotificationIntentStatuses = ["none", "requested"] as const;
export const organizationEventNotificationTypes = [
  "registrationConfirmation",
  "paymentConfirmation",
  "waitlistJoined",
  "waitlistPromotion",
  "eventPublished",
  "reminder",
  "materialUpdate",
  "urgentNotice",
  "cancellation",
  "refundConfirmation",
] as const;
export const organizationEventRegistrationStatusIds = [
  "pending",
  "confirmed",
  "waitlisted",
  "cancelled",
  "refunded",
] as const;
export const organizationEventAttendanceStatusIds = ["checkedIn"] as const;
export const organizationEventAttendanceMethodIds = ["qr", "manual"] as const;
export const organizationEventMediaTypes = ["image", "uploadedVideo", "youtube"] as const;
export const organizationEventLocationTypes = ["physical", "virtual", "hybrid"] as const;
export const organizationEventRegistrationModes = [
  "none",
  "free",
  "paid",
  "external",
  "invitation_only",
] as const;
export const organizationEventRegistrationQuestionTypes = [
  "short_text",
  "long_text",
  "single_select",
  "multi_select",
  "checkbox",
  "yes_no",
] as const;
export const organizationEventRegistrationQuestionTargets = [
  "registration",
  "each_attendee",
] as const;
export const organizationEventAttendeeTypes = ["primary", "dependent", "guest"] as const;
export const organizationEventCommunicationChannels = ["push", "email"] as const;
export const organizationEventCommunicationStatuses = [
  "queued",
  "provider_accepted",
  "failed",
  "cancelled",
] as const;
export const organizationEventSchemaVersion = 14;

export type OrganizationEventStatus = (typeof organizationEventStatusIds)[number];
export type OrganizationEventVisibility = (typeof organizationEventVisibilityIds)[number];
export type OrganizationEventType = (typeof organizationEventTypeIds)[number];
export type OrganizationEventSource = (typeof organizationEventSourceIds)[number];
export type OrganizationEventNotificationIntentStatus =
  (typeof organizationEventNotificationIntentStatuses)[number];
export type OrganizationEventNotificationType = (typeof organizationEventNotificationTypes)[number];
export type OrganizationEventRegistrationStatus =
  (typeof organizationEventRegistrationStatusIds)[number];
export type OrganizationEventAttendanceStatus =
  (typeof organizationEventAttendanceStatusIds)[number];
export type OrganizationEventAttendanceMethod =
  (typeof organizationEventAttendanceMethodIds)[number];
export type OrganizationEventMediaType = (typeof organizationEventMediaTypes)[number];
export type OrganizationEventLocationType = (typeof organizationEventLocationTypes)[number];
export type OrganizationEventRegistrationMode = (typeof organizationEventRegistrationModes)[number];
export type OrganizationEventRegistrationQuestionType =
  (typeof organizationEventRegistrationQuestionTypes)[number];
export type OrganizationEventRegistrationQuestionTarget =
  (typeof organizationEventRegistrationQuestionTargets)[number];
export type OrganizationEventAttendeeType = (typeof organizationEventAttendeeTypes)[number];
export type OrganizationEventCommunicationChannel =
  (typeof organizationEventCommunicationChannels)[number];
export type OrganizationEventCommunicationStatus =
  (typeof organizationEventCommunicationStatuses)[number];

export type OrganizationEventMediaReference = {
  storagePath: string | null;
  altText: string | null;
  altTextAr: string | null;
};

export type OrganizationEventRegistrationQuestionOption = {
  optionId: string;
  label: string;
  labelAr: string | null;
  position: number;
};

export type OrganizationEventRegistrationQuestion = {
  questionId: string;
  type: OrganizationEventRegistrationQuestionType;
  target: OrganizationEventRegistrationQuestionTarget;
  label: string;
  labelAr: string | null;
  helpText: string | null;
  helpTextAr: string | null;
  required: boolean;
  options: OrganizationEventRegistrationQuestionOption[];
  position: number;
};

export type OrganizationEventWaiverSnapshot = {
  waiverId: string;
  version: number;
  title: string;
  titleAr: string | null;
  body: string;
  bodyAr: string | null;
  hash: string;
  publishedAt: unknown;
  updatedAt: unknown;
  updatedBy: string;
};

export type OrganizationEventAttendeeSnapshot = {
  attendeeId: string;
  attendeeType: OrganizationEventAttendeeType;
  personId: string | null;
  name: string;
  relationship: string | null;
  ageGroup: string | null;
};

export type OrganizationEventRegistrationAnswer = {
  questionId: string;
  attendeeId: string | null;
  value: string | string[] | boolean | null;
};

export type OrganizationEventWaiverAcceptanceSnapshot = {
  waiverId: string;
  version: number;
  hash: string;
  acceptedAt: unknown;
  acceptedBy: string;
};

export type OrganizationEventRecord = {
  eventId: string;
  organizationId: string;
  schemaVersion?: number;
  status: OrganizationEventStatus;
  scheduledPublishAt?: unknown | null;
  visibility: OrganizationEventVisibility;
  eventType?: OrganizationEventType;
  slug?: string | null;
  categoryId?: string | null;
  organizerName?: string | null;
  title: string;
  titleAr: string | null;
  summary: string;
  summaryAr: string | null;
  description: string;
  descriptionAr: string | null;
  image: OrganizationEventMediaReference;
  coverMediaId?: string | null;
  startsAt: unknown;
  endsAt: unknown;
  allDay: boolean;
  timezone: string;
  campusId: string | null;
  campusNameSnapshot: string | null;
  locationType?: OrganizationEventLocationType;
  venueName?: string | null;
  addressLine1?: string | null;
  roomName?: string | null;
  onlineUrl?: string | null;
  onlineAccessInstructions?: string | null;
  locationName: string;
  registrationMode?: OrganizationEventRegistrationMode;
  registrationEnabled: boolean;
  registrationOpensAt: unknown | null;
  registrationClosesAt: unknown | null;
  registrationCapacity: number;
  registrationConfirmedCount: number;
  registrationQuestionCount?: number;
  registrationQuestions?: OrganizationEventRegistrationQuestion[];
  registrationCancellationAllowed: boolean;
  waitlistEnabled?: boolean;
  waitlistCount?: number;
  waiverRequired?: boolean;
  waiver?: OrganizationEventWaiverSnapshot | null;
  reminderPresets?: string[];
  paidRegistrationEnabled?: boolean;
  currency?: string | null;
  startingPriceMinor?: number | null;
  attendanceCheckedInCount: number;
  publishedAt: unknown | null;
  archivedAt: unknown | null;
  notificationRequestedOnPublish: boolean;
  notificationIntentId: string | null;
  notificationIntentStatus: OrganizationEventNotificationIntentStatus;
  createdAt: unknown;
  createdBy: string;
  updatedAt: unknown;
  updatedBy: string;
  source: OrganizationEventSource;
};

export type OrganizationEventRegistrationSource = "experience_twenty_four";
export type OrganizationEventAttendanceSource = "experience_twenty_five";

export type OrganizationEventRegistrationRecord = {
  registrationId: string;
  organizationId: string;
  eventId: string;
  personId: string;
  registrationOwnerPersonId?: string;
  status: OrganizationEventRegistrationStatus;
  attendees?: OrganizationEventAttendeeSnapshot[];
  attendeeCount?: number;
  answers?: OrganizationEventRegistrationAnswer[];
  waiverAcceptances?: OrganizationEventWaiverAcceptanceSnapshot[];
  registeredAt: unknown;
  cancelledAt: unknown | null;
  updatedAt: unknown;
  source: OrganizationEventRegistrationSource;
};

export type OrganizationEventAttendanceRecord = {
  attendanceId: string;
  organizationId: string;
  eventId: string;
  registrationId: string;
  personId: string;
  status: OrganizationEventAttendanceStatus;
  checkedInAt: unknown;
  checkedInBy: string;
  method: OrganizationEventAttendanceMethod;
  stationLabel: string | null;
  updatedAt: unknown;
  source: OrganizationEventAttendanceSource;
};

export type OrganizationEventNotificationIntentRecord = {
  intentId: string;
  organizationId: string;
  eventId: string;
  type: OrganizationEventNotificationType;
  channels: ("push" | "email")[];
  audience:
    | "allRegistrants"
    | "confirmed"
    | "waitlisted"
    | "cancelled"
    | "refunded"
    | "checkedIn"
    | "noShow";
  status: "queued" | "sent" | "failed" | "cancelled";
  createdAt: unknown;
  createdBy: string;
  updatedAt: unknown;
  updatedBy: string;
  source: "p13_events";
};

export type OrganizationEventSavedRecord = {
  savedEventId: string;
  organizationId: string;
  eventId: string;
  personId: string;
  savedAt: unknown;
  updatedAt: unknown;
  source: "p14_events";
};

export type OrganizationEventCommunicationRecord = {
  communicationId: string;
  organizationId: string;
  eventId: string;
  type: OrganizationEventNotificationType;
  audience:
    | "allRegistrants"
    | "confirmed"
    | "waitlisted"
    | "cancelled"
    | "checkedIn"
    | "notCheckedIn"
    | "connectedPeople";
  channels: OrganizationEventCommunicationChannel[];
  status: OrganizationEventCommunicationStatus;
  subject: string | null;
  subjectAr: string | null;
  message: string | null;
  messageAr: string | null;
  queuedCount: number;
  providerAcceptedCount: number;
  failedCount: number;
  idempotencyKey: string;
  createdAt: unknown;
  createdBy: string;
  updatedAt: unknown;
  updatedBy: string;
  source: "p14_events";
};

export type OrganizationEventMediaRecord = {
  mediaId: string;
  organizationId: string;
  eventId: string;
  type: OrganizationEventMediaType;
  position: number;
  title: string | null;
  titleAr?: string | null;
  altText: string | null;
  altTextAr: string | null;
  caption: string | null;
  captionAr: string | null;
  storagePath: string | null;
  originalUrl: string | null;
  thumbnailUrl: string | null;
  width: number | null;
  height: number | null;
  fileSizeBytes: number | null;
  contentType: string | null;
  playbackUrl: string | null;
  posterStoragePath: string | null;
  durationSeconds: number | null;
  videoId: string | null;
  canonicalUrl: string | null;
  createdAt: unknown;
  updatedAt: unknown;
  createdBy: string;
  updatedBy: string;
  source: "p13_events";
};

export type OrganizationEventValidationResult = {
  valid: boolean;
  errors: string[];
};

export type LocalizedOrganizationEvent = {
  title: string;
  summary: string;
  description: string;
  imageAltText: string | null;
};

const eventIdPattern = /^[a-z0-9][a-z0-9_-]{0,99}$/;
const campusIdPattern = /^[a-z0-9][a-z0-9_-]{0,99}$/;
const mediaIdPattern = /^[a-z0-9][a-z0-9_-]{0,119}$/;
const youtubeVideoIdPattern = /^[A-Za-z0-9_-]{11}$/;

export function organizationEventDocumentPath(organizationId: string, eventId: string): string {
  return `organizations/${organizationId}/events/${eventId}`;
}

export function organizationEventRegistrationDocumentPath(
  organizationId: string,
  eventId: string,
  personId: string,
): string {
  return `${organizationEventDocumentPath(organizationId, eventId)}/registrations/${personId}`;
}

export function personSavedEventDocumentPath(personId: string, savedEventId: string): string {
  return `people/${personId}/savedEvents/${savedEventId}`;
}

export function buildSavedEventId(organizationId: string, eventId: string): string {
  return `${organizationId}_${eventId}`.replace(/[^A-Za-z0-9_-]/g, "-").slice(0, 150);
}

export function organizationEventAttendanceDocumentPath(
  organizationId: string,
  eventId: string,
  registrationId: string,
): string {
  return `${organizationEventDocumentPath(organizationId, eventId)}/attendance/${registrationId}`;
}

export function organizationEventMediaDocumentPath(
  organizationId: string,
  eventId: string,
  mediaId: string,
): string {
  return `${organizationEventDocumentPath(organizationId, eventId)}/media/${mediaId}`;
}

export function organizationEventStorageRoot(organizationId: string, eventId: string): string {
  return `organizations/${organizationId}/events/${eventId}/`;
}

export function organizationEventImageStorageRoot(organizationId: string, eventId: string): string {
  return `${organizationEventStorageRoot(organizationId, eventId)}images/`;
}

export function organizationEventVideoStorageRoot(organizationId: string, eventId: string): string {
  return `${organizationEventStorageRoot(organizationId, eventId)}videos/`;
}

export function normalizeOrganizationEventId(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, "-")
    .replace(/^[^a-z0-9]+|[^a-z0-9]+$/g, "")
    .slice(0, 100);
}

export function buildOrganizationEventId(input: {
  organizationId: string;
  startsAt: Date;
  title: string;
  suffix: string;
}): string {
  const day = input.startsAt.toISOString().slice(0, 10);
  const titleSegment = normalizeOrganizationEventId(input.title).slice(0, 40) || "event";
  const suffixSegment = normalizeOrganizationEventId(input.suffix).slice(0, 16) || "draft";

  return normalizeOrganizationEventId(
    `event-${input.organizationId}-${day}-${titleSegment}-${suffixSegment}`,
  );
}

export function isFirestoreSafeOrganizationEventId(value: string): boolean {
  return eventIdPattern.test(value);
}

export function isFirestoreSafeOrganizationEventMediaId(value: string): boolean {
  return mediaIdPattern.test(value);
}

export function isOrganizationEventStatus(value: unknown): value is OrganizationEventStatus {
  return typeof value === "string" && organizationEventStatusIds.includes(value as never);
}

export function isOrganizationEventVisibility(
  value: unknown,
): value is OrganizationEventVisibility {
  return typeof value === "string" && organizationEventVisibilityIds.includes(value as never);
}

export function isOrganizationEventType(value: unknown): value is OrganizationEventType {
  return typeof value === "string" && organizationEventTypeIds.includes(value as never);
}

export function isOrganizationEventRegistrationStatus(
  value: unknown,
): value is OrganizationEventRegistrationStatus {
  return (
    typeof value === "string" && organizationEventRegistrationStatusIds.includes(value as never)
  );
}

export function isOrganizationEventAttendanceStatus(
  value: unknown,
): value is OrganizationEventAttendanceStatus {
  return typeof value === "string" && organizationEventAttendanceStatusIds.includes(value as never);
}

export function isOrganizationEventAttendanceMethod(
  value: unknown,
): value is OrganizationEventAttendanceMethod {
  return typeof value === "string" && organizationEventAttendanceMethodIds.includes(value as never);
}

export function isOrganizationEventNotificationType(
  value: unknown,
): value is OrganizationEventNotificationType {
  return typeof value === "string" && organizationEventNotificationTypes.includes(value as never);
}

export function isOrganizationEventMediaType(value: unknown): value is OrganizationEventMediaType {
  return typeof value === "string" && organizationEventMediaTypes.includes(value as never);
}

export function isOrganizationEventLocationType(
  value: unknown,
): value is OrganizationEventLocationType {
  return typeof value === "string" && organizationEventLocationTypes.includes(value as never);
}

export function isOrganizationEventRegistrationMode(
  value: unknown,
): value is OrganizationEventRegistrationMode {
  return typeof value === "string" && organizationEventRegistrationModes.includes(value as never);
}

export function isOrganizationEventRegistrationQuestionType(
  value: unknown,
): value is OrganizationEventRegistrationQuestionType {
  return (
    typeof value === "string" && organizationEventRegistrationQuestionTypes.includes(value as never)
  );
}

export function isOrganizationEventRegistrationQuestionTarget(
  value: unknown,
): value is OrganizationEventRegistrationQuestionTarget {
  return (
    typeof value === "string" &&
    organizationEventRegistrationQuestionTargets.includes(value as never)
  );
}

export function isOrganizationEventAttendeeType(
  value: unknown,
): value is OrganizationEventAttendeeType {
  return typeof value === "string" && organizationEventAttendeeTypes.includes(value as never);
}

export function isOrganizationEventStoragePath(
  organizationId: string,
  eventId: string,
  value: string | null,
): boolean {
  if (value === null) {
    return true;
  }

  return (
    value.length <= 512 &&
    value.startsWith(organizationEventStorageRoot(organizationId, eventId)) &&
    !value.includes("..") &&
    !value.endsWith("/")
  );
}

export function isOrganizationEventImageStoragePath(
  organizationId: string,
  eventId: string,
  value: string | null,
): boolean {
  if (value === null) {
    return true;
  }

  return (
    isOrganizationEventStoragePath(organizationId, eventId, value) &&
    (value.startsWith(organizationEventImageStorageRoot(organizationId, eventId)) ||
      isLegacyEventRootMediaPath(organizationId, eventId, value))
  );
}

export function isOrganizationEventVideoStoragePath(
  organizationId: string,
  eventId: string,
  value: string | null,
): boolean {
  if (value === null) {
    return true;
  }

  return (
    isOrganizationEventStoragePath(organizationId, eventId, value) &&
    value.startsWith(organizationEventVideoStorageRoot(organizationId, eventId))
  );
}

export function normalizeYouTubeVideoUrl(value: string): {
  canonicalUrl: string;
  videoId: string;
} | null {
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  let url: URL;
  try {
    url = new URL(trimmed);
  } catch {
    try {
      url = new URL(`https://${trimmed}`);
    } catch {
      return null;
    }
  }

  const hostname = url.hostname.toLowerCase().replace(/^www\./, "");
  let videoId: string | null = null;

  if (hostname === "youtu.be") {
    videoId = url.pathname.split("/").filter(Boolean)[0] ?? null;
  } else if (hostname === "youtube.com" || hostname === "m.youtube.com") {
    if (url.pathname === "/watch") {
      videoId = url.searchParams.get("v");
    } else {
      const parts = url.pathname.split("/").filter(Boolean);
      if (parts[0] === "shorts" || parts[0] === "embed" || parts[0] === "live") {
        videoId = parts[1] ?? null;
      }
    }
  }

  if (!videoId || !youtubeVideoIdPattern.test(videoId)) {
    return null;
  }

  return {
    canonicalUrl: `https://www.youtube.com/watch?v=${videoId}`,
    videoId,
  };
}

export function localizedOrganizationEvent(
  event: OrganizationEventRecord,
  locale: OrganizationLocaleCode,
): LocalizedOrganizationEvent {
  const useArabic = locale === "ar";

  return {
    description: useArabic
      ? fallbackText(event.descriptionAr, event.description)
      : event.description,
    imageAltText: useArabic
      ? fallbackNullableText(event.image.altTextAr, event.image.altText)
      : event.image.altText,
    summary: useArabic ? fallbackText(event.summaryAr, event.summary) : event.summary,
    title: useArabic ? fallbackText(event.titleAr, event.title) : event.title,
  };
}

export function validateOrganizationEventRecord(event: unknown): OrganizationEventValidationResult {
  const errors: string[] = [];

  if (!isRecord(event)) {
    return { errors: ["Organization event must be an object."], valid: false };
  }

  const eventId = stringValue(event.eventId);
  const organizationId = stringValue(event.organizationId);

  if (!isFirestoreSafeOrganizationId(organizationId)) {
    errors.push("Organization event organizationId must be Firestore-safe.");
  }

  if (!isFirestoreSafeOrganizationEventId(eventId)) {
    errors.push("Organization event eventId must be Firestore-safe.");
  }

  if (!isOrganizationEventStatus(event.status)) {
    errors.push("Organization event status must be supported.");
  }

  if (!isOrganizationEventVisibility(event.visibility)) {
    errors.push("Organization event visibility must be supported.");
  }

  if (event.eventType !== undefined && !isOrganizationEventType(event.eventType)) {
    errors.push("Organization event type must be supported.");
  }

  validateOptionalSchemaVersion(event.schemaVersion, errors);
  validateOptionalText("Event slug", event.slug, 140, errors);
  validateOptionalText("Event category", event.categoryId, 80, errors);
  validateOptionalText("Event organizer name", event.organizerName, 180, errors);
  validateRequiredText("English title", event.title, 140, errors);
  validateOptionalText("Arabic title", event.titleAr, 140, errors);
  validateRequiredText("English summary", event.summary, 300, errors);
  validateOptionalText("Arabic summary", event.summaryAr, 300, errors);
  validateRequiredText("English description", event.description, 3000, errors);
  validateOptionalText("Arabic description", event.descriptionAr, 3000, errors);
  validateImageReference(event.image, organizationId, eventId, errors);
  validateOptionalText("Cover media id", event.coverMediaId, 120, errors);
  validateEventSchedule(event.startsAt, event.endsAt, event.allDay, event.timezone, errors);
  validateCampusReference(event.campusId, event.campusNameSnapshot, errors);
  validateEventLocation(event, errors);
  validateRequiredText("Location", event.locationName, 180, errors);
  validateEventRegistrationConfig(event, errors);
  validateEventAttendanceAggregate(event, errors);

  if (
    (event.status === "published" ||
      event.status === "registration_closed" ||
      event.status === "completed") &&
    event.publishedAt === null
  ) {
    errors.push("Published organization event states require publishedAt.");
  }

  if ((event.status === "draft" || event.status === "scheduled") && event.publishedAt !== null) {
    errors.push("Draft or scheduled organization events must not carry publishedAt.");
  }

  if (event.status === "scheduled" && event.scheduledPublishAt == null) {
    errors.push("Scheduled organization events require scheduledPublishAt.");
  }

  if (event.status !== "scheduled" && event.scheduledPublishAt != null) {
    errors.push("Only scheduled organization events may carry scheduledPublishAt.");
  }

  if ((event.status === "archived" || event.status === "cancelled") && event.archivedAt === null) {
    errors.push("Archived or cancelled organization events require archivedAt.");
  }

  if (event.status !== "archived" && event.status !== "cancelled" && event.archivedAt !== null) {
    errors.push("Only archived or cancelled organization events may carry archivedAt.");
  }

  if (typeof event.notificationRequestedOnPublish !== "boolean") {
    errors.push("Organization event notificationRequestedOnPublish must be a boolean.");
  }

  validateOptionalText("Notification intent id", event.notificationIntentId, 140, errors);

  if (
    !organizationEventNotificationIntentStatuses.includes(
      event.notificationIntentStatus as OrganizationEventNotificationIntentStatus,
    )
  ) {
    errors.push("Organization event notification intent status must be none or requested.");
  }

  validateRequiredText("Created by", event.createdBy, 150, errors);
  validateRequiredText("Updated by", event.updatedBy, 150, errors);

  if (!organizationEventSourceIds.includes(event.source as OrganizationEventSource)) {
    errors.push("Organization event source must be supported.");
  }

  return { errors, valid: errors.length === 0 };
}

export function validateOrganizationEventMediaRecord(
  media: unknown,
): OrganizationEventValidationResult {
  const errors: string[] = [];

  if (!isRecord(media)) {
    return { errors: ["Organization event media must be an object."], valid: false };
  }

  const mediaId = stringValue(media.mediaId);
  const organizationId = stringValue(media.organizationId);
  const eventId = stringValue(media.eventId);
  const type = media.type;
  const storagePath = stringOrNull(media.storagePath);
  const originalUrl = stringOrNull(media.originalUrl);
  const playbackUrl = stringOrNull(media.playbackUrl);
  const canonicalUrl = stringOrNull(media.canonicalUrl);
  const videoId = stringOrNull(media.videoId);

  if (!isFirestoreSafeOrganizationId(organizationId)) {
    errors.push("Event media organizationId must be Firestore-safe.");
  }

  if (!isFirestoreSafeOrganizationEventId(eventId)) {
    errors.push("Event media eventId must be Firestore-safe.");
  }

  if (!isFirestoreSafeOrganizationEventMediaId(mediaId)) {
    errors.push("Event media mediaId must be Firestore-safe.");
  }

  if (!isOrganizationEventMediaType(type)) {
    errors.push("Event media type must be image, uploadedVideo, or youtube.");
  }

  const position = numberValue(media.position);
  if (!Number.isInteger(media.position) || position < 0 || position > 1000) {
    errors.push("Event media position must be an integer between 0 and 1000.");
  }

  validateOptionalText("Event media title", media.title, 180, errors);
  validateOptionalText("Event media Arabic title", media.titleAr, 180, errors);
  validateOptionalText("Event media alt text", media.altText, 220, errors);
  validateOptionalText("Event media Arabic alt text", media.altTextAr, 220, errors);
  validateOptionalText("Event media caption", media.caption, 300, errors);
  validateOptionalText("Event media Arabic caption", media.captionAr, 300, errors);
  validateOptionalText("Event media original URL", media.originalUrl, 1000, errors);
  validateOptionalText("Event media thumbnail URL", media.thumbnailUrl, 1000, errors);
  validateOptionalText("Event media playback URL", media.playbackUrl, 1000, errors);
  validateOptionalText("Event media content type", media.contentType, 120, errors);
  validateRequiredText("Event media created by", media.createdBy, 150, errors);
  validateRequiredText("Event media updated by", media.updatedBy, 150, errors);

  validateNullablePositiveInteger("Event media width", media.width, 20000, errors);
  validateNullablePositiveInteger("Event media height", media.height, 20000, errors);
  validateNullablePositiveInteger(
    "Event media file size",
    media.fileSizeBytes,
    500_000_000,
    errors,
  );
  validateNullablePositiveInteger(
    "Event media duration seconds",
    media.durationSeconds,
    24 * 60 * 60,
    errors,
  );

  if (type === "image") {
    if (!isOrganizationEventImageStoragePath(organizationId, eventId, storagePath)) {
      errors.push(
        "Event image media storage path must stay under the organization event images path.",
      );
    }
    if (storagePath === null && originalUrl === null) {
      errors.push("Event image media requires a storage path or original URL.");
    }
    if (videoId !== null || canonicalUrl !== null || playbackUrl !== null) {
      errors.push("Event image media must not contain video identifiers or playback URL.");
    }
  }

  if (type === "uploadedVideo") {
    if (!isOrganizationEventVideoStoragePath(organizationId, eventId, storagePath)) {
      errors.push(
        "Uploaded event video storage path must stay under the organization event videos path.",
      );
    }
    if (storagePath === null && playbackUrl === null) {
      errors.push("Uploaded event video requires a storage path or playback URL.");
    }
    if (videoId !== null || canonicalUrl !== null) {
      errors.push("Uploaded event video must not contain YouTube identifiers.");
    }
  }

  if (type === "youtube") {
    const normalized = canonicalUrl ? normalizeYouTubeVideoUrl(canonicalUrl) : null;
    if (
      !videoId ||
      !youtubeVideoIdPattern.test(videoId) ||
      !normalized ||
      normalized.videoId !== videoId
    ) {
      errors.push("YouTube event media requires a normalized canonical video URL and ID.");
    }
    if (storagePath !== null || playbackUrl !== null) {
      errors.push("YouTube event media must not store upload storage or arbitrary playback URLs.");
    }
  }

  if (media.source !== "p13_events") {
    errors.push("Event media source must be p13_events.");
  }

  if (timestampMillis(media.createdAt) === null) {
    errors.push("Event media createdAt is required.");
  }

  if (timestampMillis(media.updatedAt) === null) {
    errors.push("Event media updatedAt is required.");
  }

  return { errors, valid: errors.length === 0 };
}

export function validateOrganizationEventRegistrationRecord(
  registration: unknown,
): OrganizationEventValidationResult {
  const errors: string[] = [];

  if (!isRecord(registration)) {
    return { errors: ["Organization event registration must be an object."], valid: false };
  }

  const registrationId = stringValue(registration.registrationId);
  const organizationId = stringValue(registration.organizationId);
  const eventId = stringValue(registration.eventId);
  const personId = stringValue(registration.personId);

  if (!isFirestoreSafeOrganizationId(organizationId)) {
    errors.push("Event registration organizationId must be Firestore-safe.");
  }

  if (!isFirestoreSafeOrganizationEventId(eventId)) {
    errors.push("Event registration eventId must be Firestore-safe.");
  }

  if (!isValidRegistrationActorId(registrationId)) {
    errors.push("Event registration registrationId must be a valid document id.");
  }

  if (!isValidRegistrationActorId(personId)) {
    errors.push("Event registration personId must be a valid document id.");
  }

  if (registrationId !== personId) {
    errors.push("Event registration registrationId must match personId.");
  }

  const ownerPersonId = stringValue(registration.registrationOwnerPersonId ?? personId);
  if (!isValidRegistrationActorId(ownerPersonId)) {
    errors.push("Event registration owner personId must be a valid document id.");
  }

  if (!isOrganizationEventRegistrationStatus(registration.status)) {
    errors.push("Event registration status must be confirmed or cancelled.");
  }

  validateAttendees(registration.attendees, personId, errors);

  if (
    registration.attendeeCount !== undefined &&
    (!Number.isInteger(registration.attendeeCount) ||
      numberValue(registration.attendeeCount) < 1 ||
      numberValue(registration.attendeeCount) > 100)
  ) {
    errors.push("Event registration attendeeCount must be between 1 and 100.");
  }

  if (Array.isArray(registration.attendees) && registration.attendeeCount !== undefined) {
    const attendeeCount = numberValue(registration.attendeeCount);
    if (attendeeCount !== registration.attendees.length) {
      errors.push("Event registration attendeeCount must match attendee snapshots.");
    }
  }

  validateRegistrationAnswers(registration.answers, errors);
  validateWaiverAcceptances(registration.waiverAcceptances, errors);

  if (timestampMillis(registration.registeredAt) === null) {
    errors.push("Event registration registeredAt is required.");
  }

  if (timestampMillis(registration.updatedAt) === null) {
    errors.push("Event registration updatedAt is required.");
  }

  if (registration.status === "confirmed" && registration.cancelledAt !== null) {
    errors.push("Confirmed event registrations must not carry cancelledAt.");
  }

  if (registration.status === "cancelled" && timestampMillis(registration.cancelledAt) === null) {
    errors.push("Cancelled event registrations require cancelledAt.");
  }

  if (registration.source !== "experience_twenty_four") {
    errors.push("Event registration source must be experience_twenty_four.");
  }

  return { errors, valid: errors.length === 0 };
}

export function validateOrganizationEventAttendanceRecord(
  attendance: unknown,
): OrganizationEventValidationResult {
  const errors: string[] = [];

  if (!isRecord(attendance)) {
    return { errors: ["Organization event attendance must be an object."], valid: false };
  }

  const attendanceId = stringValue(attendance.attendanceId);
  const organizationId = stringValue(attendance.organizationId);
  const eventId = stringValue(attendance.eventId);
  const registrationId = stringValue(attendance.registrationId);
  const personId = stringValue(attendance.personId);

  if (!isFirestoreSafeOrganizationId(organizationId)) {
    errors.push("Event attendance organizationId must be Firestore-safe.");
  }

  if (!isFirestoreSafeOrganizationEventId(eventId)) {
    errors.push("Event attendance eventId must be Firestore-safe.");
  }

  if (!isValidRegistrationActorId(attendanceId)) {
    errors.push("Event attendance attendanceId must be a valid document id.");
  }

  if (!isValidRegistrationActorId(registrationId)) {
    errors.push("Event attendance registrationId must be a valid document id.");
  }

  if (!isValidRegistrationActorId(personId)) {
    errors.push("Event attendance personId must be a valid document id.");
  }

  if (attendanceId !== registrationId) {
    errors.push("Event attendance attendanceId must match registrationId.");
  }

  if (!isOrganizationEventAttendanceStatus(attendance.status)) {
    errors.push("Event attendance status must be checkedIn.");
  }

  if (timestampMillis(attendance.checkedInAt) === null) {
    errors.push("Event attendance checkedInAt is required.");
  }

  validateRequiredText("Event attendance checkedInBy", attendance.checkedInBy, 150, errors);

  if (!isOrganizationEventAttendanceMethod(attendance.method)) {
    errors.push("Event attendance method must be qr or manual.");
  }

  validateOptionalText("Event attendance station label", attendance.stationLabel, 120, errors);

  if (timestampMillis(attendance.updatedAt) === null) {
    errors.push("Event attendance updatedAt is required.");
  }

  if (attendance.source !== "experience_twenty_five") {
    errors.push("Event attendance source must be experience_twenty_five.");
  }

  return { errors, valid: errors.length === 0 };
}

export function validatePersonSavedEventRecord(saved: unknown): OrganizationEventValidationResult {
  const errors: string[] = [];

  if (!isRecord(saved)) {
    return { errors: ["Saved event must be an object."], valid: false };
  }

  const savedEventId = stringValue(saved.savedEventId);
  const organizationId = stringValue(saved.organizationId);
  const eventId = stringValue(saved.eventId);
  const personId = stringValue(saved.personId);

  if (!isValidRegistrationActorId(savedEventId)) {
    errors.push("Saved event id must be a valid document id.");
  }

  if (!isFirestoreSafeOrganizationId(organizationId)) {
    errors.push("Saved event organizationId must be Firestore-safe.");
  }

  if (!isFirestoreSafeOrganizationEventId(eventId)) {
    errors.push("Saved event eventId must be Firestore-safe.");
  }

  if (!isValidRegistrationActorId(personId)) {
    errors.push("Saved event personId must be a valid document id.");
  }

  if (savedEventId !== buildSavedEventId(organizationId, eventId)) {
    errors.push("Saved event id must match organization and event.");
  }

  if (timestampMillis(saved.savedAt) === null) {
    errors.push("Saved event savedAt is required.");
  }

  if (timestampMillis(saved.updatedAt) === null) {
    errors.push("Saved event updatedAt is required.");
  }

  if (saved.source !== "p14_events") {
    errors.push("Saved event source must be p14_events.");
  }

  return { errors, valid: errors.length === 0 };
}

function validateEventRegistrationConfig(event: Record<string, unknown>, errors: string[]): void {
  const opensAtMillis = timestampMillis(event.registrationOpensAt);
  const closesAtMillis = timestampMillis(event.registrationClosesAt);
  const registrationCapacity = numberValue(event.registrationCapacity);
  const registrationConfirmedCount = numberValue(event.registrationConfirmedCount);

  if (typeof event.registrationEnabled !== "boolean") {
    errors.push("Organization event registrationEnabled must be a boolean.");
  }

  if (event.registrationOpensAt !== null && opensAtMillis === null) {
    errors.push("Organization event registrationOpensAt must be a timestamp or null.");
  }

  if (event.registrationClosesAt !== null && closesAtMillis === null) {
    errors.push("Organization event registrationClosesAt must be a timestamp or null.");
  }

  if (opensAtMillis !== null && closesAtMillis !== null && opensAtMillis >= closesAtMillis) {
    errors.push("Organization event registrationClosesAt must be after registrationOpensAt.");
  }

  if (!Number.isInteger(event.registrationCapacity)) {
    errors.push("Organization event registrationCapacity must be an integer.");
  } else if (
    (event.registrationEnabled && registrationCapacity <= 0) ||
    registrationCapacity < 0 ||
    registrationCapacity > 100000
  ) {
    errors.push(
      "Organization event registrationCapacity must be valid for the registration state.",
    );
  }

  if (!Number.isInteger(event.registrationConfirmedCount)) {
    errors.push("Organization event registrationConfirmedCount must be an integer.");
  } else if (registrationConfirmedCount < 0 || registrationConfirmedCount > registrationCapacity) {
    errors.push("Organization event registrationConfirmedCount must fit within capacity.");
  }

  if (typeof event.registrationCancellationAllowed !== "boolean") {
    errors.push("Organization event registrationCancellationAllowed must be a boolean.");
  }

  if (
    event.registrationMode !== undefined &&
    !isOrganizationEventRegistrationMode(event.registrationMode)
  ) {
    errors.push("Organization event registrationMode must be supported.");
  }

  if (event.waitlistEnabled !== undefined && typeof event.waitlistEnabled !== "boolean") {
    errors.push("Organization event waitlistEnabled must be a boolean.");
  }

  if (
    event.waitlistCount !== undefined &&
    (!Number.isInteger(event.waitlistCount) || numberValue(event.waitlistCount) < 0)
  ) {
    errors.push("Organization event waitlistCount must be a non-negative integer.");
  }

  if (
    event.paidRegistrationEnabled !== undefined &&
    typeof event.paidRegistrationEnabled !== "boolean"
  ) {
    errors.push("Organization event paidRegistrationEnabled must be a boolean.");
  }

  validateOptionalText("Organization event currency", event.currency, 3, errors);

  if (
    event.startingPriceMinor !== undefined &&
    event.startingPriceMinor !== null &&
    (!Number.isInteger(event.startingPriceMinor) || numberValue(event.startingPriceMinor) < 0)
  ) {
    errors.push("Organization event startingPriceMinor must be a non-negative integer or null.");
  }

  if (
    event.registrationQuestionCount !== undefined &&
    (!Number.isInteger(event.registrationQuestionCount) ||
      numberValue(event.registrationQuestionCount) < 0 ||
      numberValue(event.registrationQuestionCount) > 50)
  ) {
    errors.push("Organization event registrationQuestionCount must be between 0 and 50.");
  }

  validateRegistrationQuestions(event.registrationQuestions, errors);

  if (event.waiverRequired !== undefined && typeof event.waiverRequired !== "boolean") {
    errors.push("Organization event waiverRequired must be a boolean.");
  }

  validateEventWaiver(event.waiver, errors);

  if (event.reminderPresets !== undefined) {
    if (!Array.isArray(event.reminderPresets) || event.reminderPresets.length > 3) {
      errors.push("Organization event reminderPresets must contain at most three presets.");
    } else {
      for (const preset of event.reminderPresets) {
        if (
          preset !== "one_day_before" &&
          preset !== "two_hours_before" &&
          preset !== "event_start"
        ) {
          errors.push("Organization event reminderPresets contains an unsupported preset.");
        }
      }
    }
  }
}

function validateEventAttendanceAggregate(event: Record<string, unknown>, errors: string[]): void {
  const attendanceCheckedInCount = numberValue(event.attendanceCheckedInCount);
  const registrationCapacity = numberValue(event.registrationCapacity);

  if (!Number.isInteger(event.attendanceCheckedInCount)) {
    errors.push("Organization event attendanceCheckedInCount must be an integer.");
  } else if (
    attendanceCheckedInCount < 0 ||
    attendanceCheckedInCount > Math.max(0, registrationCapacity)
  ) {
    errors.push("Organization event attendanceCheckedInCount must fit within capacity.");
  }
}

function validateRegistrationQuestions(value: unknown, errors: string[]): void {
  if (value === undefined) {
    return;
  }

  if (!Array.isArray(value)) {
    errors.push("Organization event registrationQuestions must be an array.");
    return;
  }

  if (value.length > 50) {
    errors.push("Organization event registrationQuestions may contain at most 50 questions.");
  }

  const seen = new Set<string>();
  for (const question of value) {
    if (!isRecord(question)) {
      errors.push("Organization event registrationQuestions entries must be objects.");
      continue;
    }

    const questionId = stringValue(question.questionId);
    if (!isValidRegistrationActorId(questionId)) {
      errors.push("Registration question id must be a valid document id.");
    } else if (seen.has(questionId)) {
      errors.push("Registration question ids must be unique.");
    }
    seen.add(questionId);

    if (!isOrganizationEventRegistrationQuestionType(question.type)) {
      errors.push("Registration question type is unsupported.");
    }
    if (!isOrganizationEventRegistrationQuestionTarget(question.target)) {
      errors.push("Registration question target is unsupported.");
    }
    validateRequiredText("Registration question label", question.label, 180, errors);
    validateOptionalText("Arabic registration question label", question.labelAr, 180, errors);
    validateOptionalText("Registration question help text", question.helpText, 300, errors);
    validateOptionalText(
      "Arabic registration question help text",
      question.helpTextAr,
      300,
      errors,
    );
    if (typeof question.required !== "boolean") {
      errors.push("Registration question required must be a boolean.");
    }
    if (!Number.isInteger(question.position) || numberValue(question.position) < 0) {
      errors.push("Registration question position must be a non-negative integer.");
    }
    validateRegistrationQuestionOptions(question.options, question.type, errors);
  }
}

function validateRegistrationQuestionOptions(
  value: unknown,
  type: unknown,
  errors: string[],
): void {
  if (!Array.isArray(value)) {
    errors.push("Registration question options must be an array.");
    return;
  }

  const requiresOptions = type === "single_select" || type === "multi_select";
  if (requiresOptions && value.length === 0) {
    errors.push("Select registration questions require options.");
  }
  if (!requiresOptions && value.length > 0) {
    errors.push("Only select registration questions may define options.");
  }
  if (value.length > 40) {
    errors.push("Registration question options may contain at most 40 entries.");
  }

  const seen = new Set<string>();
  for (const option of value) {
    if (!isRecord(option)) {
      errors.push("Registration question options must be objects.");
      continue;
    }
    const optionId = stringValue(option.optionId);
    if (!isValidRegistrationActorId(optionId)) {
      errors.push("Registration question option id must be a valid document id.");
    } else if (seen.has(optionId)) {
      errors.push("Registration question option ids must be unique.");
    }
    seen.add(optionId);
    validateRequiredText("Registration question option label", option.label, 120, errors);
    validateOptionalText("Arabic registration question option label", option.labelAr, 120, errors);
    if (!Number.isInteger(option.position) || numberValue(option.position) < 0) {
      errors.push("Registration question option position must be a non-negative integer.");
    }
  }
}

function validateEventWaiver(value: unknown, errors: string[]): void {
  if (value === undefined || value === null) {
    return;
  }

  if (!isRecord(value)) {
    errors.push("Organization event waiver must be an object or null.");
    return;
  }

  validateRequiredText("Event waiver id", value.waiverId, 120, errors);
  if (!Number.isInteger(value.version) || numberValue(value.version) < 1) {
    errors.push("Event waiver version must be a positive integer.");
  }
  validateRequiredText("Event waiver title", value.title, 180, errors);
  validateOptionalText("Arabic event waiver title", value.titleAr, 180, errors);
  validateRequiredText("Event waiver body", value.body, 12000, errors);
  validateOptionalText("Arabic event waiver body", value.bodyAr, 12000, errors);
  validateRequiredText("Event waiver hash", value.hash, 128, errors);
  if (timestampMillis(value.publishedAt) === null) {
    errors.push("Event waiver publishedAt is required.");
  }
  if (timestampMillis(value.updatedAt) === null) {
    errors.push("Event waiver updatedAt is required.");
  }
  validateRequiredText("Event waiver updated by", value.updatedBy, 150, errors);
}

function validateAttendees(value: unknown, ownerPersonId: string, errors: string[]): void {
  if (value === undefined) {
    return;
  }

  if (!Array.isArray(value)) {
    errors.push("Event registration attendees must be an array.");
    return;
  }

  if (value.length < 1 || value.length > 100) {
    errors.push("Event registration attendees must contain between 1 and 100 attendees.");
  }

  const seen = new Set<string>();
  let hasPrimary = false;
  for (const attendee of value) {
    if (!isRecord(attendee)) {
      errors.push("Event registration attendee entries must be objects.");
      continue;
    }
    const attendeeId = stringValue(attendee.attendeeId);
    if (!isValidRegistrationActorId(attendeeId)) {
      errors.push("Event attendee id must be a valid document id.");
    } else if (seen.has(attendeeId)) {
      errors.push("Event attendee ids must be unique.");
    }
    seen.add(attendeeId);
    if (!isOrganizationEventAttendeeType(attendee.attendeeType)) {
      errors.push("Event attendee type is unsupported.");
    }
    if (attendee.attendeeType === "primary") {
      hasPrimary = true;
      if (attendee.personId !== ownerPersonId) {
        errors.push("Primary event attendee must reference the registration owner.");
      }
    }
    if (attendee.attendeeType === "guest" && attendee.personId !== null) {
      errors.push("Guest event attendees must not reference a person profile.");
    }
    if (attendee.personId !== null && !isValidRegistrationActorId(stringValue(attendee.personId))) {
      errors.push("Event attendee personId must be null or a valid document id.");
    }
    validateRequiredText("Event attendee name", attendee.name, 160, errors);
    validateOptionalText("Event attendee relationship", attendee.relationship, 80, errors);
    validateOptionalText("Event attendee age group", attendee.ageGroup, 60, errors);
  }

  if (!hasPrimary) {
    errors.push("Event registration attendees must include the primary attendee.");
  }
}

function validateRegistrationAnswers(value: unknown, errors: string[]): void {
  if (value === undefined) {
    return;
  }

  if (!Array.isArray(value)) {
    errors.push("Event registration answers must be an array.");
    return;
  }

  if (value.length > 200) {
    errors.push("Event registration answers may contain at most 200 entries.");
  }

  for (const answer of value) {
    if (!isRecord(answer)) {
      errors.push("Event registration answers entries must be objects.");
      continue;
    }
    validateRequiredText("Event registration answer questionId", answer.questionId, 150, errors);
    if (answer.attendeeId !== null && answer.attendeeId !== undefined) {
      validateRequiredText("Event registration answer attendeeId", answer.attendeeId, 150, errors);
    }
    if (!isValidAnswerValue(answer.value)) {
      errors.push("Event registration answer value is invalid.");
    }
  }
}

function validateWaiverAcceptances(value: unknown, errors: string[]): void {
  if (value === undefined) {
    return;
  }

  if (!Array.isArray(value)) {
    errors.push("Event waiver acceptances must be an array.");
    return;
  }

  if (value.length > 5) {
    errors.push("Event waiver acceptances may contain at most five entries.");
  }

  for (const acceptance of value) {
    if (!isRecord(acceptance)) {
      errors.push("Event waiver acceptance entries must be objects.");
      continue;
    }
    validateRequiredText("Event waiver acceptance waiverId", acceptance.waiverId, 120, errors);
    if (!Number.isInteger(acceptance.version) || numberValue(acceptance.version) < 1) {
      errors.push("Event waiver acceptance version must be a positive integer.");
    }
    validateRequiredText("Event waiver acceptance hash", acceptance.hash, 128, errors);
    validateRequiredText("Event waiver acceptance acceptedBy", acceptance.acceptedBy, 150, errors);
    if (timestampMillis(acceptance.acceptedAt) === null) {
      errors.push("Event waiver acceptance acceptedAt is required.");
    }
  }
}

function isValidAnswerValue(value: unknown): boolean {
  if (value === null || typeof value === "boolean") {
    return true;
  }
  if (typeof value === "string") {
    return value.length <= 4000;
  }
  return (
    Array.isArray(value) && value.every((entry) => typeof entry === "string" && entry.length <= 400)
  );
}

function validateImageReference(
  image: unknown,
  organizationId: string,
  eventId: string,
  errors: string[],
): void {
  if (!isRecord(image)) {
    errors.push("Organization event image must be a media reference object.");
    return;
  }

  const storagePath = image.storagePath === null ? null : stringValue(image.storagePath);
  if (!isOrganizationEventStoragePath(organizationId, eventId, storagePath)) {
    errors.push("Event image storage path must stay under the organization event path.");
  }

  validateOptionalText("Image alt text", image.altText, 180, errors);
  validateOptionalText("Arabic image alt text", image.altTextAr, 180, errors);
}

function validateEventLocation(event: Record<string, unknown>, errors: string[]): void {
  if (event.locationType !== undefined && !isOrganizationEventLocationType(event.locationType)) {
    errors.push("Organization event locationType must be physical, virtual, or hybrid.");
  }

  validateOptionalText("Venue name", event.venueName, 180, errors);
  validateOptionalText("Address", event.addressLine1, 240, errors);
  validateOptionalText("Room name", event.roomName, 120, errors);
  validateOptionalText("Online URL", event.onlineUrl, 1000, errors);
  validateOptionalText("Online access instructions", event.onlineAccessInstructions, 1200, errors);
}

function validateEventSchedule(
  startsAt: unknown,
  endsAt: unknown,
  allDay: unknown,
  timezone: unknown,
  errors: string[],
): void {
  const startsAtMillis = timestampMillis(startsAt);
  const endsAtMillis = timestampMillis(endsAt);

  if (startsAtMillis === null) {
    errors.push("Event start time is required.");
  }

  if (endsAtMillis === null) {
    errors.push("Event end time is required.");
  }

  if (startsAtMillis !== null && endsAtMillis !== null && startsAtMillis >= endsAtMillis) {
    errors.push("Event end time must be after start time.");
  }

  if (allDay !== false) {
    errors.push("EXP-023 events must explicitly set allDay to false.");
  }

  validateRequiredText("Timezone", timezone, 80, errors);
}

function validateCampusReference(
  campusId: unknown,
  campusNameSnapshot: unknown,
  errors: string[],
): void {
  if (campusId === null) {
    if (campusNameSnapshot !== null) {
      errors.push("Campus snapshot must be null when campusId is null.");
    }
    return;
  }

  if (typeof campusId !== "string" || !campusIdPattern.test(campusId)) {
    errors.push("Campus id must be null or Firestore-safe text.");
  }

  validateRequiredText("Campus name snapshot", campusNameSnapshot, 180, errors);
}

function timestampMillis(value: unknown): number | null {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value.getTime();
  }

  if (typeof value === "string") {
    const millis = Date.parse(value);
    return Number.isNaN(millis) ? null : millis;
  }

  if (isRecord(value) && typeof value.toMillis === "function") {
    const millis = value.toMillis();
    return typeof millis === "number" && Number.isFinite(millis) ? millis : null;
  }

  if (isRecord(value) && typeof value.seconds === "number") {
    return value.seconds * 1000;
  }

  return null;
}

function fallbackText(value: string | null, fallback: string): string {
  return value && value.trim().length > 0 ? value : fallback;
}

function fallbackNullableText(value: string | null, fallback: string | null): string | null {
  if (value && value.trim().length > 0) {
    return value;
  }

  return fallback && fallback.trim().length > 0 ? fallback : null;
}

function validateRequiredText(
  label: string,
  value: unknown,
  maxLength: number,
  errors: string[],
): void {
  if (typeof value !== "string" || value.trim().length === 0) {
    errors.push(`${label} is required.`);
    return;
  }

  if (value.length > maxLength) {
    errors.push(`${label} must be ${maxLength} characters or fewer.`);
  }
}

function validateOptionalText(
  label: string,
  value: unknown,
  maxLength: number,
  errors: string[],
): void {
  if (value === null || value === undefined) {
    return;
  }

  if (typeof value !== "string") {
    errors.push(`${label} must be text or null.`);
    return;
  }

  if (value.length > maxLength) {
    errors.push(`${label} must be ${maxLength} characters or fewer.`);
  }
}

function validateOptionalSchemaVersion(value: unknown, errors: string[]): void {
  if (value === undefined) {
    return;
  }

  if (!Number.isInteger(value) || numberValue(value) < 1) {
    errors.push("Organization event schemaVersion must be a positive integer.");
  }
}

function validateNullablePositiveInteger(
  label: string,
  value: unknown,
  max: number,
  errors: string[],
): void {
  if (value === null) {
    return;
  }

  if (!Number.isInteger(value) || numberValue(value) < 0 || numberValue(value) > max) {
    errors.push(`${label} must be a non-negative integer no larger than ${max}.`);
  }
}

function stringOrNull(value: unknown): string | null {
  return typeof value === "string" ? value : null;
}

function stringValue(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function numberValue(value: unknown): number {
  return typeof value === "number" && Number.isFinite(value) ? value : -1;
}

function isValidRegistrationActorId(value: string): boolean {
  return value.length > 0 && value.length <= 150 && !value.includes("/");
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isLegacyEventRootMediaPath(
  organizationId: string,
  eventId: string,
  value: string,
): boolean {
  const suffix = value.slice(organizationEventStorageRoot(organizationId, eventId).length);
  return suffix.length > 0 && !suffix.includes("/");
}
